import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';

/** One write in flight; new edits are coalesced, never sent with an old revision. */
export function useModelDraft() {
  const [project, setProject] = useState(null);
  const [inputs, setInputs] = useState(null);
  const [save, setSave] = useState('idle');
  const [saveError, setSaveError] = useState('');
  const state = useRef({ project: null, inputs: null, dirty: false, generation: 0, blocked: false });
  const flight = useRef(null); const timer = useRef(null);

  const adopt = useCallback((p) => {
    clearTimeout(timer.current);
    state.current = { project: p, inputs: p.inputs, dirty: false, generation: 0, blocked: false };
    setProject(p); setInputs(p.inputs); setSave('idle'); setSaveError('');
  }, []);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    if (flight.current) return flight.current;
    if (state.current.blocked) return Promise.reject(new Error('Reload the latest version before saving.'));
    const run = async () => {
      while (state.current.dirty && state.current.project) {
        const current = state.current; const generation = current.generation;
        setSave('saving'); setSaveError('');
        try {
          const p = await api.patchProject(current.project.id, { revision: current.project.revision, inputs: current.inputs });
          current.project = p; setProject(p);
          if (current.generation === generation) current.dirty = false;
        } catch (e) {
          current.blocked = e.status === 409;
          setSave(current.blocked ? 'conflict' : 'error'); setSaveError(e.message);
          throw e;
        }
      }
      setSave('saved'); return state.current.project;
    };
    flight.current = run().finally(() => { flight.current = null; });
    return flight.current;
  }, []);

  const update = useCallback((patch) => {
    const current = state.current;
    current.inputs = { ...current.inputs, ...patch }; current.dirty = true; current.generation += 1;
    setInputs(current.inputs);
    if (!current.blocked) {
      setSave('pending'); clearTimeout(timer.current);
      timer.current = setTimeout(() => { flush().catch(() => {}); }, 800);
    }
  }, [flush]);

  useEffect(() => {
    const before = (e) => { if (state.current.dirty) { e.preventDefault(); e.returnValue = ''; } };
    const links = (e) => {
      const link = e.target.closest?.('a[href]');
      if (state.current.dirty && link && !window.confirm('Changes are not saved yet. Leave and discard these edits?')) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('beforeunload', before); document.addEventListener('click', links, true);
    return () => { clearTimeout(timer.current); window.removeEventListener('beforeunload', before); document.removeEventListener('click', links, true); };
  }, []);
  return { project, inputs, save, saveError, adopt, update, flush };
}